import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useStores } from "netbank-shared/src/hooks";
import { IMessageInboxPage } from "netbank-shared/src/libs/models/Content/Page";
import { tx } from "netbank-shared/src/libs/i18n";
import { InboxTabs } from "netbank-shared/src/libs/models/Message/Inbox";
import { Button, LinkButton, Table } from "~views/shared";
import styles from "./MessageInbox.scss";
import rootStyles from "../Root.scss";
import { Message } from "./Message";
import trashcan from "~assets/trashcan_gray.svg";
import { HtmlContent } from "~views/shared/HtmlContent/HtmlContent";
import writeIcon from "~assets/write.svg";
import { TrackingAction, TrackingCategory } from "netbank-shared/src/libs/models/Tracking";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs } from "~views/shared/Tabs/Tabs";
import classNames from "classnames";
import { IThread } from "netbank-shared/src/libs/models/Message/Threads";
import { IColumn } from "~views/shared/Table/Table";

export const MessageInbox = observer(() => {
  const { contentStore, uiStore, messageStore } = useStores();

  const [searchParams] = useSearchParams();
  const page = contentStore.currentPage as IMessageInboxPage;

  const [frontendIndex, setFrontendIndex] = useState<number>(messageStore.pageSize);
  const [fetchingMore, setFetchingMore] = useState(false);

  const hasNextPage = messageStore.threads && messageStore.threads.length > frontendIndex;
  const threadIdFromUrl = searchParams.get("threadId");

  const fetchMore = async () => {
    setFetchingMore(true);
    setFrontendIndex(frontendIndex + messageStore.pageSize);
    setFetchingMore(false);
  };

  const spinnerClasses = [rootStyles.spinner, styles.spinner];
  const messageClasses = [rootStyles.noBorder, rootStyles.accordion];

  if (uiStore.isMobile) {
    messageClasses.push(rootStyles.noPadding);
  }

  if (fetchingMore) {
    spinnerClasses.push(rootStyles.loading);
  }

  useEffect(() => {
    const fetchData = async () => {
      if (threadIdFromUrl) {
        await messageStore.fetchThread(threadIdFromUrl);
      }
      if (!threadIdFromUrl && !messageStore.threads) {
        await messageStore.fetchThreads();
      }
    };
    fetchData();

    return () => messageStore.resetStore();
  }, [threadIdFromUrl]);

  return (
    <>
      {page.pageDescription && (
        <section className={styles.pageDescription}>
          <HtmlContent html={page.pageDescription} />
        </section>
      )}
      {threadIdFromUrl && <Message data={messageStore.threadData} />}
      {!threadIdFromUrl && (
        <section className={messageClasses.join(" ")}>
          <Tabs
            activePanel={messageStore.activeTab}
            items={[
              {
                key: InboxTabs.inbox,
                label: tx("message.inbox"),
                children: <MessagesTabPanel frontendIndex={frontendIndex} />,
              },
              {
                key: InboxTabs.sent,
                label: tx("message.sent"),
                children: <MessagesTabPanel frontendIndex={frontendIndex} />,
              },
              {
                key: InboxTabs.archive,
                label: tx("message.archive"),
                children: <MessagesTabPanel frontendIndex={frontendIndex} />,
              },
            ]}
            onChange={messageStore.setActiveTab}
            extra={
              page?.newMessagePage ? (
                <LinkButton
                  to={page?.newMessagePage}
                  trackingCategory={TrackingCategory.Messages}
                  trackingAction={TrackingAction.NewMessage}
                  fullWidth={uiStore.isSm}
                  iconPrefix={uiStore.isSm && writeIcon}
                >
                  {page.newMessageButtonText ?? tx("message.composeNewMessage")}
                </LinkButton>
              ) : undefined
            }
            loading={messageStore.fetchingThreads}
            footer={
              hasNextPage ? (
                <div onClick={fetchMore} className={[rootStyles.loadMoreRow, styles.loadMoreWrapper].join(" ")}>
                  <span>{page.loadMoreButtonText || tx("message.loadMore")}</span>
                  <span className={spinnerClasses.join(" ")} />
                </div>
              ) : undefined
            }
          />
        </section>
      )}
    </>
  );
});

interface MessagesTabPanelProps {
  frontendIndex: number;
}
const MessagesTabPanel = (props: MessagesTabPanelProps) => {
  const { frontendIndex } = props;

  const { contentStore, messageStore } = useStores();
  const navigate = useNavigate();

  const [archivingIds, setArchivingIds] = useState<string[]>([]);
  const page = contentStore.currentPage as IMessageInboxPage;

  const columns: IColumn<IThread>[] = [
    {
      width: 110,
      title: tx("misc.date"),
      accessor: "created",
      render: (_, v) => {
        const createdDate = v.messages[0]?.created.split("T")[0] || "";
        return (
          <div
            className={classNames({
              [styles.unread]: !v.customerAccessed && messageStore.activeTab !== InboxTabs.sent,
            })}
          >
            {createdDate}
          </div>
        );
      },
    },
    {
      title: tx("misc.message"),
      ellipsis: true,
      accessor: "messages",
      render: (_, v) => {
        const messageBody = v.messages.find((m) => !!m.body)?.body || "";

        return (
          <div className={styles.messageContent}>
            <div className={styles.tag}>{v.customerAccessed ? tx("misc.read") : tx("misc.unread")}</div>
            <div
              className={classNames(styles.title, {
                [styles.unread]: !v.customerAccessed && messageStore.activeTab !== InboxTabs.sent,
              })}
            >
              {v.title}
            </div>
            <div className={styles.message}>{messageBody.split("\n").join(" ")}</div>
          </div>
        );
      },
    },
  ];

  if (messageStore.activeTab !== InboxTabs.archive) {
    columns.push({
      width: 90,
      title: "",
      accessor: "messages",
      align: "center",
      render: (_, msg) =>
        msg.archivedByCustomer ? undefined : (
          <Button
            className={styles.archiveButton}
            type="text"
            iconPrefix={trashcan}
            title={tx("message.toArchive")}
            loading={archivingIds.includes(msg.threadId)}
            onClick={async (e) => {
              e.stopPropagation();

              try {
                setArchivingIds((prev) => [...prev, msg.threadId]);
                await messageStore.archive(msg.threadId);
                await messageStore.fetchThreads(false);
              } finally {
                setArchivingIds((prev) => prev.filter((x) => x !== msg.threadId));
              }
            }}
          />
        ),
    });
  }

  return (
    <div className={styles.messages}>
      <Table
        data={messageStore.threads?.slice(0, frontendIndex) || []}
        onRowClick={(row) => navigate(`${page.messageInboxPage}?threadId=${row.threadId}`)}
        columns={columns}
      />
      <div>
        {(!messageStore.threads || messageStore.threads.length === 0) && (
          <div className={styles.emptyInboxWrapper}>
            <span className={styles.emptyInboxHeader}>{page.inboxEmptyStateHeader || tx("message.inboxEmpty")}</span>
            <HtmlContent className={styles.emptyInboxText} html={page.inboxEmptyStateText} />
          </div>
        )}
      </div>
    </div>
  );
};
